'use client';

import Image from "next/image";

interface WorkflowCardProps {
  title: string;
  description: string;
  thumbnail?: string;
  onClick?: () => void;
}

export default function WorkflowCard({ title, description, thumbnail, onClick }: WorkflowCardProps) {
  // 检查是否是有效的图片URL
  const isValidImageUrl = thumbnail && (
    thumbnail.startsWith('http') || 
    thumbnail.startsWith('/') || 
    thumbnail.startsWith('data:')
  );

  return (
    <button
      onClick={onClick}
      className="h-[160px] p-4 bg-white/5 border border-white/10 rounded-xl
                 hover:border-white/20 hover:bg-white/8 hover:-translate-y-0.5
                 transition-all duration-200
                 flex text-left group overflow-hidden"
    >
      {/* 左侧文字内容区域 */}
      <div className="flex-1 flex flex-col justify-between pr-3 min-w-0">
        <div>
          <h3 className="text-[15px] font-medium text-white mb-2 line-clamp-1">{title}</h3>
          <p className="text-[13px] text-white/50 line-clamp-3 leading-relaxed">{description}</p>
        </div>
      </div>
      
      {/* 右侧竖屏图片/GIF区域 - 固定宽度，高度撑满 */}
      <div className="w-18 h-full shrink-0">
        <div 
          className="w-full h-full rounded-lg bg-white/10 
                     overflow-hidden border border-white/5
                     group-hover:border-white/20 transition-all duration-200
                     flex items-center justify-center"
        >
          {isValidImageUrl ? (
            <Image 
              src={thumbnail} 
              alt={title} 
              width={72} 
              height={128}
              className="w-full h-full object-cover"
              unoptimized={thumbnail.endsWith('.gif')} // GIF不优化以保持动画
            />
          ) : (
            // 默认占位图 - 渐变背景
            <div className="w-full h-full bg-linear-to-br from-white/10 to-white/5 
                           flex items-center justify-center">
              <svg 
                className="w-6 h-6 text-white/30" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="1.5"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// 默认工作流列表 - 使用在线图片资源展示
export const defaultWorkflows = [
  { 
    title: "模特工作流", 
    description: "快速生成模特穿搭图片，支持多种风格和场景，一键换装换背景", 
    thumbnail: "https://picsum.photos/seed/model/144/256" 
  },
  { 
    title: "产品拍摄", 
    description: "AI智能商拍，一键生成专业产品图，支持场景自动匹配", 
    thumbnail: "https://picsum.photos/seed/product/144/256" 
  },
  { 
    title: "文案生成", 
    description: "根据产品特点自动生成营销文案，支持多种文风", 
    thumbnail: "https://picsum.photos/seed/writing/144/256" 
  },
  { 
    title: "数据分析", 
    description: "智能分析销售数据，生成可视化报告，发现增长机会", 
    thumbnail: "https://picsum.photos/seed/data/144/256" 
  },
  { 
    title: "客服助手", 
    description: "AI客服自动回复，提升服务效率，7x24小时在线", 
    thumbnail: "https://picsum.photos/seed/service/144/256" 
  },
  { 
    title: "选品推荐", 
    description: "基于市场数据的智能选品建议，洞察爆款趋势", 
    thumbnail: "https://picsum.photos/seed/recommend/144/256" 
  },
  { 
    title: "详情页生成", 
    description: "一键生成电商详情页设计，多模板风格可选", 
    thumbnail: "https://picsum.photos/seed/detail/144/256" 
  },
  { 
    title: "视频脚本", 
    description: "AI生成短视频脚本和分镜，提升内容创作效率", 
    thumbnail: "https://picsum.photos/seed/video/144/256" 
  },
  { 
    title: "买家秀生成", 
    description: "智能生成真实买家秀图片，提升转化率", 
    thumbnail: "https://picsum.photos/seed/buyer/144/256" 
  },
  { 
    title: "竞品分析", 
    description: "深度分析竞品数据，制定差异化策略", 
    thumbnail: "https://picsum.photos/seed/compete/144/256" 
  },
  { 
    title: "价格优化", 
    description: "基于市场数据的智能定价建议，最大化利润", 
    thumbnail: "https://picsum.photos/seed/price/144/256" 
  },
  { 
    title: "库存预测", 
    description: "AI预测销量趋势，优化库存周转效率", 
    thumbnail: "https://picsum.photos/seed/stock/144/256" 
  },
];
